import { useState } from 'react'
import { useAdminContacts, useForceVoidContact } from '../../hooks/useAdmin'
import { SPECIES_LABELS, STATUS_LABELS, CONTACT_STATUSES } from '../../constants/taxonomy'
import { STATUS_COLORS } from '../../components/map/confidenceStyle'
import type { ContactSummary, SpeciesBucket } from '../../types/contact'

function VoidControl({ contact }: { contact: ContactSummary }) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const mutation = useForceVoidContact()

  if (contact.voided) {
    return <span className="text-xs text-gray-400">Voided</span>
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs text-red-600 hover:text-red-700 font-medium"
      >
        Void
      </button>
    )
  }

  return (
    <div className="flex items-center gap-1.5">
      <input
        type="text"
        placeholder="Reason (optional)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        className="w-36 rounded border border-gray-300 text-xs px-1.5 py-1"
      />
      <button
        type="button"
        disabled={mutation.isPending}
        onClick={() => mutation.mutate({ contactId: contact.id, reason }, { onSuccess: () => setOpen(false) })}
        className="text-xs text-white bg-red-600 hover:bg-red-500 disabled:opacity-50 rounded px-2 py-1 font-medium"
      >
        Confirm
      </button>
      <button
        type="button"
        onClick={() => setOpen(false)}
        className="text-xs text-gray-500 hover:text-gray-700"
      >
        Cancel
      </button>
    </div>
  )
}

export default function AdminContactsPage() {
  const [status, setStatus] = useState('')
  const [voidedOnly, setVoidedOnly] = useState(false)
  const { data, isLoading, isError } = useAdminContacts({
    status: status || undefined,
    voided_only: voidedOnly,
  })

  return (
    <div>
      <h1 className="text-lg font-semibold text-gray-900 mb-1">Contacts</h1>
      <p className="text-sm text-gray-500 mb-4">
        Unlike the public map, this includes voided contacts. Voiding here is direct and
        immediate — it doesn't touch the contributing reports, unlike rejecting a report.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded-lg border border-gray-300 text-sm px-2 py-1.5"
        >
          <option value="">All statuses</option>
          {CONTACT_STATUSES.map((s) => (
            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={voidedOnly}
            onChange={(e) => setVoidedOnly(e.target.checked)}
          />
          Voided only
        </label>
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading contacts…</p>}
      {isError && <p className="text-sm text-red-600">Failed to load contacts.</p>}

      {data && (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs font-medium text-gray-500">
                <th className="px-3 py-2">Species</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Confidence</th>
                <th className="px-3 py-2">Reports</th>
                <th className="px-3 py-2">Updated</th>
                <th className="px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.map((c) => (
                <tr key={c.id} className="border-b border-gray-100 last:border-0">
                  <td className="px-3 py-2 text-gray-900">
                    {SPECIES_LABELS[c.species_bucket as SpeciesBucket] ?? c.species_bucket}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium"
                      style={{ color: STATUS_COLORS[c.status], backgroundColor: STATUS_COLORS[c.status] + '1a' }}
                    >
                      {STATUS_LABELS[c.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-gray-600">{c.confidence_score.toFixed(1)}</td>
                  <td className="px-3 py-2 text-gray-600">{c.report_count}</td>
                  <td className="px-3 py-2 text-gray-500">
                    {new Date(c.last_updated_at).toLocaleString()}
                  </td>
                  <td className="px-3 py-2">
                    <VoidControl contact={c} />
                  </td>
                </tr>
              ))}
              {data.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-gray-500">
                    No contacts match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
